import { siteConfig } from "@/config/site";

export interface SchemaEventData {
  id: string;
  title: string;
  slug: string;
  venue: string;
  city: string;
  date: string;
  end_date: string | null;
  img_url: string | null;
  description: string | null;
  main_artist?: string | null;
  state?: string | null;
  event_artists?: Array<{
    is_main_artist: boolean;
    artists: {
      id: number;
      artist: string;
      slug: string;
    };
  }>;
}

export const getImageSrc = (imgUrl: string | null): string => {
  if (!imgUrl) return "/images/default.jpg";
  if (imgUrl.startsWith("http")) return imgUrl;
  return `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/event-images/${imgUrl}`;
};

export const generateEventSchema = (
  event: SchemaEventData,
  citySlug: string = "",
  eventSlug: string = "",
) => {
  const SCHEMA_ORG = "https://schema.org";
  const mainArtist =
    event.main_artist ||
    event.event_artists?.find((ea) => ea.is_main_artist)?.artists.artist ||
    null;

  const eventTitle =
    mainArtist && event.title
      ? `${mainArtist} - ${event.title}`
      : mainArtist || event.title;

  // Use the provided slugs or derive from event properties
  const pageSlug = eventSlug || event.slug;
  const pageCitySlug = citySlug || "";
  const url = pageCitySlug
    ? `${siteConfig.url}/${pageCitySlug}/${pageSlug}`
    : `${siteConfig.url}/${pageSlug}`;

  return {
    "@context": SCHEMA_ORG,
    "@type": "MusicEvent",
    name: eventTitle,
    startDate: event.date,
    endDate: event.end_date || event.date,
    location: {
      "@type": "Place",
      name: event.venue,
      address: {
        "@type": "PostalAddress",
        addressLocality: event.city,
        addressRegion: event.state || "",
        addressCountry: "US",
      },
    },
    image: getImageSrc(event.img_url),
    url: url,
    eventStatus: `${SCHEMA_ORG}/EventScheduled`,
    eventAttendanceMode: `${SCHEMA_ORG}/OfflineEventAttendanceMode`,
    ...(event.description && { description: event.description }),
    ...(mainArtist && {
      performer: {
        "@type": "MusicGroup",
        name: mainArtist,
      },
    }),
  };
};
